import axios from 'axios';

const getHeaders = (token?: string) => ({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

// ── Tenant CRUD ──────────────────────────────────────────

export interface TenantDto {
  Oid: string;
  Name: string;
  CompanyName: string;
  IsActive: boolean;
  IsActivated: boolean;
  CreatedDate: string;
  DeactivatedDate: string | null;
  ContactEmail: string;
  ConnectionString: string;
  MaxUsers: number;
  TrialStartDate: string | null;
  TrialEndDate: string | null;
  IsTrialActive: boolean;
  TrialDaysRemaining: number;
}

export interface CreateTenantRequest {
  name: string;
  companyName: string;
  contactEmail?: string;
  notes?: string;
}

export async function listTenants(token?: string): Promise<TenantDto[]> {
  const res = await axios.get('/api/Tenant/List', getHeaders(token));
  return res.data;
}

export async function createTenant(request: CreateTenantRequest, token?: string) {
  const res = await axios.post('/api/Tenant/Create', request, getHeaders(token));
  return res.data;
}

export async function deactivateTenant(tenantOid: string, token?: string) {
  const res = await axios.post(`/api/Tenant/Deactivate/${tenantOid}`, null, getHeaders(token));
  return res.data;
}

export async function reactivateTenant(tenantOid: string, token?: string) {
  const res = await axios.post(`/api/Tenant/Reactivate/${tenantOid}`, null, getHeaders(token));
  return res.data;
}

export async function deleteTenant(tenantOid: string, dropDatabase: boolean, token?: string) {
  const res = await axios.delete(`/api/Tenant/Delete/${tenantOid}?dropDatabase=${dropDatabase}`, getHeaders(token));
  return res.data;
}

export async function resendTenantActivation(email: string, token?: string) {
  const res = await axios.post('/api/Registration/ResendActivation', { email }, getHeaders(token));
  return res.data;
}

// ── Tenant User Management ───────────────────────────────

export interface CreateTenantUserRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export async function createTenantUser(
  tenantOid: string,
  request: CreateTenantUserRequest,
  token?: string,
) {
  const res = await axios.post(`/api/Tenant/${tenantOid}/CreateUser`, request, getHeaders(token));
  return res.data;
}

// ── Accounting Provider ──────────────────────────────────

export interface AccountingProviderDto {
  ProviderType: string;
  ExactOnlineClientId: string;
  ExactOnlineCallbackUrl: string;
  HasRefreshToken: boolean;
  HasSnelStartSubscriptionKey: boolean;
  HasSnelStartClientKey: boolean;
  HasSnelStartConnection: boolean;
  SnelStartSalesOrderTemplateId: string | null;
}

export interface ConfigureAccountingProviderRequest {
  providerType: string;
  exactOnlineClientId?: string;
  exactOnlineClientSecret?: string;
  exactOnlineCallbackUrl?: string;
  snelStartSubscriptionKey?: string;
  snelStartClientKey?: string;
  snelStartSalesOrderTemplateId?: string | null;
}

export async function getSnelStartSalesOrderTemplates(tenantOid: string, token?: string) {
  const res = await axios.get(`/api/Tenant/${tenantOid}/SnelStart/SalesOrderTemplates`, getHeaders(token));
  return res.data;
}

export async function getAccountingProvider(tenantOid: string, token?: string): Promise<AccountingProviderDto> {
  const res = await axios.get(`/api/Tenant/${tenantOid}/AccountingProvider`, getHeaders(token));
  return res.data;
}

export async function configureAccountingProvider(
  tenantOid: string,
  request: ConfigureAccountingProviderRequest,
  token?: string,
) {
  const res = await axios.put(`/api/Tenant/${tenantOid}/AccountingProvider`, request, getHeaders(token));
  return res.data;
}

export async function removeAccountingProvider(tenantOid: string, token?: string) {
  const res = await axios.delete(`/api/Tenant/${tenantOid}/AccountingProvider`, getHeaders(token));
  return res.data;
}

export function getOAuthInitiateUrl(tenantOid: string): string {
  return `/api/Tenant/${tenantOid}/OAuthInitiate`;
}

// ── Tenant Users ─────────────────────────────────────────

export interface TenantUserDto {
  Oid: string;
  UserName: string;
  FirstName: string;
  LastName: string;
  IsActive: boolean;
}

export async function listTenantUsers(tenantOid: string, token?: string): Promise<TenantUserDto[]> {
  const res = await axios.get(`/api/Tenant/${tenantOid}/Users`, getHeaders(token));
  return res.data;
}

// ── Tenant Settings ──────────────────────────────────────

export interface UpdateTenantSettingsRequest {
  maxUsers?: number;
  trialEndDate?: string;
  contactEmail?: string;
  notes?: string;
}

export async function updateTenantSettings(
  tenantOid: string,
  request: UpdateTenantSettingsRequest,
  token?: string,
) {
  const res = await axios.put(`/api/Tenant/${tenantOid}/Settings`, request, getHeaders(token));
  return res.data;
}

export async function syncTenantEmployees(tenantOid: string, token?: string) {
  const res = await axios.post(`/api/Tenant/${tenantOid}/SyncEmployees`, {}, getHeaders(token));
  return res.data;
}
